import { Block } from '../classes/Block';

export type BlockId = string;
export type CardId = string;

export interface CardGroup {
  id: CardId;
  name: string;
  color: string;
  blockIds: BlockId[];
}

const CARD_COLORS: readonly string[] = [
  '#5b8dd9',
  // '#d97c5b',
  '#9b59b6',
  // '#e67e22',
  '#1abc9c',
  '#e74c3c',
  '#f39c12',
  '#27ae60',
];

export function generateCardId(): CardId {
  return crypto.randomUUID();
}

export function generateCardColor(index: number): string {
  return CARD_COLORS[index % CARD_COLORS.length];
}

/** Assigns round-robin colors to all card groups by their index. Ensures no adjacent cards share the same color. */
function assignRoundRobinColors(groups: CardGroup[]): CardGroup[] {
  return groups.map((g, i) => ({ ...g, color: generateCardColor(i) }));
}

export function initializeCardGroups(blocks: Block[]): CardGroup[] {
  if (blocks.length === 0) return [];
  return assignRoundRobinColors([{
    id: generateCardId(),
    name: 'Card 1',
    color: CARD_COLORS[0],
    blockIds: blocks.map(b => b.id),
  }]);
}

function renumberCardNames(groups: CardGroup[]): CardGroup[] {
  return groups.map((g, i) => ({ ...g, name: `Card ${i + 1}` }));
}

export function normalizeCardGroups(
  groups: CardGroup[],
  blocks: Block[]
): CardGroup[] {
  if (groups.length === 0) return groups;

  const validBlockIds = new Set(blocks.map(b => b.id));
  const blockIndexMap = new Map(blocks.map((b, i) => [b.id, i]));

  const result: CardGroup[] = [];

  for (const group of groups) {
    const validIds = group.blockIds.filter(id => validBlockIds.has(id));
    if (validIds.length === 0) continue;

    const sortedByIndex = [...validIds].sort(
      (a, b) => (blockIndexMap.get(a) ?? 0) - (blockIndexMap.get(b) ?? 0)
    );

    const runs: BlockId[][] = [[sortedByIndex[0]]];
    for (let i = 1; i < sortedByIndex.length; i++) {
      const prevIdx = blockIndexMap.get(sortedByIndex[i - 1]) ?? -2;
      const currIdx = blockIndexMap.get(sortedByIndex[i]) ?? -1;
      if (currIdx === prevIdx + 1) {
        runs[runs.length - 1].push(sortedByIndex[i]);
      } else {
        runs.push([sortedByIndex[i]]);
      }
    }

    for (let r = 0; r < runs.length; r++) {
      result.push({
        id: r === 0 ? group.id : generateCardId(),
        name: r === 0 ? group.name : '',
        color: r === 0 ? group.color : generateCardColor(result.length),
        blockIds: runs[r],
      });
    }
  }

  const assignedIds = new Set(result.flatMap(g => g.blockIds));
  const unassigned = blocks.filter(b => !assignedIds.has(b.id));

  if (unassigned.length > 0 && result.length > 0) {
    result[result.length - 1] = {
      ...result[result.length - 1],
      blockIds: [...result[result.length - 1].blockIds, ...unassigned.map(b => b.id)],
    };
  } else if (unassigned.length > 0) {
    result.push({
      id: generateCardId(),
      name: 'Card 1',
      color: CARD_COLORS[0],
      blockIds: unassigned.map(b => b.id),
    });
  }

  result.sort((a, b) => {
    const aIdx = blockIndexMap.get(a.blockIds[0]) ?? 0;
    const bIdx = blockIndexMap.get(b.blockIds[0]) ?? 0;
    return aIdx - bIdx;
  });

  return assignRoundRobinColors(renumberCardNames(result));
}

export function splitCardAtBlock(
  groups: CardGroup[],
  blockId: BlockId,
  blocks: Block[]
): CardGroup[] {
  const blockIndexMap = new Map(blocks.map((b, i) => [b.id, i]));
  const result: CardGroup[] = [];

  for (const group of groups) {
    const splitIdx = group.blockIds.indexOf(blockId);
    if (splitIdx <= 0) {
      result.push({ ...group, blockIds: [...group.blockIds] });
    } else {
      result.push({
        id: group.id,
        name: group.name,
        color: group.color,
        blockIds: group.blockIds.slice(0, splitIdx),
      });
      result.push({
        id: generateCardId(),
        name: '',
        color: generateCardColor(result.length),
        blockIds: group.blockIds.slice(splitIdx),
      });
    }
  }

  result.sort((a, b) => {
    const aIdx = blockIndexMap.get(a.blockIds[0]) ?? 0;
    const bIdx = blockIndexMap.get(b.blockIds[0]) ?? 0;
    return aIdx - bIdx;
  });

  return assignRoundRobinColors(renumberCardNames(result));
}

export function mergeCardWithPrevious(
  groups: CardGroup[],
  cardId: CardId
): CardGroup[] {
  const idx = groups.findIndex(g => g.id === cardId);
  if (idx <= 0) return groups;

  const result = groups.map(g => ({ ...g, blockIds: [...g.blockIds] }));
  result[idx - 1].blockIds = [...result[idx - 1].blockIds, ...result[idx].blockIds];
  result.splice(idx, 1);
  return assignRoundRobinColors(renumberCardNames(result));
}

export function ungroupCard(
  groups: CardGroup[],
  cardId: CardId
): CardGroup[] {
  if (groups.length <= 1) return groups;

  const idx = groups.findIndex(g => g.id === cardId);
  if (idx === -1) return groups;

  const result = groups.map(g => ({ ...g, blockIds: [...g.blockIds] }));
  if (idx > 0) {
    result[idx - 1].blockIds = [...result[idx - 1].blockIds, ...result[idx].blockIds];
    result.splice(idx, 1);
  } else {
    result[1].blockIds = [...result[0].blockIds, ...result[1].blockIds];
    result.splice(0, 1);
  }
  return assignRoundRobinColors(renumberCardNames(result));
}

export function getBlockCardGroup(
  groups: CardGroup[],
  blockId: BlockId
): CardGroup | undefined {
  return groups.find(g => g.blockIds.includes(blockId));
}

export function buildBlockCardMap(groups: CardGroup[]): Map<BlockId, CardGroup> {
  const map = new Map<BlockId, CardGroup>();
  for (const group of groups) {
    for (const blockId of group.blockIds) {
      map.set(blockId, group);
    }
  }
  return map;
}

export function buildBlockCardIndexMap(groups: CardGroup[]): Map<BlockId, number> {
  const map = new Map<BlockId, number>();
  groups.forEach((group, cardIndex) => {
    for (const blockId of group.blockIds) {
      map.set(blockId, cardIndex);
    }
  });
  return map;
}

export function rebuildCardGroupsFromIndices(
  blocks: Block[],
  blockCardIndices: Map<number, number>
): CardGroup[] {
  if (blocks.length === 0) return [];

  const groups: CardGroup[] = [];
  let lastCardIndex: number = blockCardIndices.get(0) ?? 0;
  let currentBlockIds: BlockId[] = [blocks[0].id];

  for (let i = 1; i < blocks.length; i++) {
    const block = blocks[i];
    const cardIndex = blockCardIndices.get(i) ?? lastCardIndex;

    if (cardIndex !== lastCardIndex) {
      groups.push({
        id: generateCardId(),
        name: `Card ${groups.length + 1}`,
        color: generateCardColor(groups.length),
        blockIds: [...currentBlockIds],
      });
      currentBlockIds = [];
      lastCardIndex = cardIndex;
    }
    currentBlockIds.push(block.id);
  }

  groups.push({
    id: generateCardId(),
    name: `Card ${groups.length + 1}`,
    color: generateCardColor(groups.length),
    blockIds: currentBlockIds,
  });

  return assignRoundRobinColors(groups);
}
